import { useEffect, useState, useRef } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { fetchPageBySlug } from '../lib/cms'
import { useDocumentMeta } from '../hooks/useDocumentMeta'

export default function DynamicPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const [page, setPage] = useState(null)
  const [loading, setLoading] = useState(true)
  const [activeHeading, setActiveHeading] = useState('')
  const observer = useRef(null)

  const metaTitle = page?.meta_titel || (page?.titel ? `${page.titel} | Terapien.dk` : 'Terapien.dk')
  const metaDescription = page?.meta_beskrivelse || 'Læs mere på Terapien.dk - Psykoterapeut Birgitte Vilsgaard i Svendborg.'

  useDocumentMeta({
    title: metaTitle,
    description: metaDescription
  })

  // Map route path to CMS url_slug
  const getSlugFromPath = (pathname) => {
    if (pathname === '/privacy-policy') return 'privacy-policy'
    if (pathname === '/cookie-policy-eu') return 'cookie-policy-eu'
    if (pathname === '/psykoterapi') return 'hvad-er-psykoterapi'
    return null
  }

  useEffect(() => {
    async function loadPageData() {
      setLoading(true)
      const slug = getSlugFromPath(location.pathname)
      if (!slug) {
        navigate('/404')
        return
      }

      const pageData = await fetchPageBySlug(slug)
      if (!pageData) {
        navigate('/404')
        return
      }

      setPage(pageData)
      setLoading(false)
    }
    loadPageData()
  }, [location.pathname, navigate])

  // Set up intersection observer for policy page headings
  useEffect(() => {
    if (loading || !page || page.url_slug === 'hvad-er-psykoterapi') return

    const handleIntersect = (entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActiveHeading(entry.target.id)
        }
      })
    }

    observer.current = new IntersectionObserver(handleIntersect, {
      rootMargin: '-100px 0px -70% 0px',
    })

    const headings = document.querySelectorAll('.prose h2')
    headings.forEach((h) => observer.current.observe(h))

    return () => {
      if (observer.current) {
        observer.current.disconnect()
      }
    }
  }, [loading, page])

  if (loading) {
    return (
      <div className="pt-[72px] min-h-screen flex items-center justify-center bg-brand-offwhite">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-brand-sage" />
      </div>
    )
  }

  const isTherapyPage = page.url_slug === 'hvad-er-psykoterapi'

  // Helper to parse therapy page content into structured sections
  const parseTherapyContent = (htmlString) => {
    if (!htmlString) return null
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, 'text/html')
    const children = Array.from(doc.body.children)

    const introElements = []
    let methodsHeading = "Metoder jeg benytter i min terapi"
    const methods = []
    let currentMethod = null
    let reachedMethods = false

    children.forEach((el) => {
      const tagName = el.tagName.toLowerCase()

      if (tagName === 'h2') {
        reachedMethods = true
        methodsHeading = el.innerHTML
        return
      }

      if (!reachedMethods) {
        introElements.push(el.outerHTML)
      } else {
        if (tagName === 'h3') {
          if (currentMethod) {
            methods.push(currentMethod)
          }
          currentMethod = {
            title: el.innerHTML,
            paragraphs: []
          }
        } else {
          if (currentMethod) {
            currentMethod.paragraphs.push(el.outerHTML)
          } else {
            introElements.push(el.outerHTML)
          }
        }
      }
    })

    if (currentMethod) {
      methods.push(currentMethod)
    }

    return {
      introHtml: introElements.join(''),
      methodsHeading,
      methods
    }
  }

  // Helper to parse policy page content and add IDs to h2s
  const parsePolicyContent = (htmlString) => {
    if (!htmlString) return null
    const parser = new DOMParser()
    const doc = parser.parseFromString(htmlString, 'text/html')
    const children = Array.from(doc.body.children)

    const headings = []
    children.forEach((el, index) => {
      if (el.tagName.toLowerCase() === 'h2') {
        const id = `section-${index}`
        el.setAttribute('id', id)
        headings.push({
          id,
          text: el.textContent
        })
      }
    })

    return {
      html: doc.body.innerHTML,
      headings
    }
  }

  const getMethodIcon = (title) => {
    const t = title.toLowerCase()
    if (t.includes('organisk') || t.includes('psykodynamisk')) return 'fa-seedling'
    if (t.includes('eksistentiel')) return 'fa-compass'
    if (t.includes('narrativ')) return 'fa-feather-pointed'
    if (t.includes('systemisk')) return 'fa-diagram-project'
    if (t.includes('kognitiv')) return 'fa-brain'
    if (t.includes('mindfulness')) return 'fa-spa'
    if (t.includes('adfærd')) return 'fa-shield-heart'
    return 'fa-circle-dot'
  }

  // Render Therapy Page
  if (isTherapyPage) {
    const parsed = parseTherapyContent(page.indhold)

    return (
      <div className="pt-[72px] min-h-screen bg-brand-offwhite">
        {/* Hero Section */}
        <section className="relative bg-brand-dark text-brand-offwhite py-20 lg:py-28 overflow-hidden">
          {/* Background Image with Overlay */}
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/dc1f0a52-cd31-4baa-b917-63870f7a10df/shutterstock-1756005818.jpg"
              alt="Psykoterapi - Terapien.dk"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/85 to-transparent" />

          <div className="max-w-7xl mx-auto px-6 relative z-10 flex flex-col gap-4">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-brand-sage">
              <i className="fa-solid fa-heart-pulse text-[10px]" /> Min Terapeutiske Tilgang
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-sand leading-tight">
              {page.titel}
            </h1>
            {page.meta_beskrivelse && (
              <p className="text-base sm:text-lg font-serif italic text-brand-offwhite/85 max-w-3xl leading-relaxed">
                {page.meta_beskrivelse}
              </p>
            )}
          </div>
        </section>

        {/* Intro & Sidebar Section */}
        <section className="py-16 lg:py-24">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
              {/* Left: Intro Text */}
              <div className="lg:col-span-7 space-y-8">
                <div
                  className="prose text-brand-gray/90 text-base sm:text-lg"
                  dangerouslySetInnerHTML={{ __html: parsed?.introHtml }}
                />
              </div>

              {/* Right: Sticky Sidebar Card */}
              <div className="lg:col-span-5 lg:sticky lg:top-28">
                <div className="bg-brand-sand/20 border border-brand-sand/50 rounded-3xl p-8 sm:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.01)] space-y-8">
                  <div className="space-y-4">
                    <span className="text-xs uppercase tracking-[0.2em] text-brand-sage font-bold block">
                      Tidsbestilling
                    </span>
                    <h3 className="text-2xl font-serif font-bold text-brand-dark">
                      Klar til at tage det første skridt?
                    </h3>
                    <p className="text-brand-gray/80 text-sm sm:text-base leading-relaxed">
                      Du er altid velkommen til at kontakte mig for en uforpligtende samtale eller hvis du har spørgsmål til terapien.
                    </p>
                  </div>

                  {/* Contact Info List */}
                  <div className="space-y-4 border-t border-brand-sand/40 pt-6">
                    <a
                      href="tel:28839488"
                      className="flex items-center gap-4 p-3 rounded-2xl hover:bg-brand-offwhite/80 transition-all duration-300 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-brand-sage/10 flex items-center justify-center text-brand-sage group-hover:bg-brand-sage group-hover:text-brand-offwhite transition-all duration-300">
                        <i className="fa-solid fa-phone text-sm" />
                      </div>
                      <div>
                        <span className="text-xs text-brand-gray/50 block">Ring direkte</span>
                        <span className="text-brand-dark font-medium group-hover:text-brand-sage transition-colors">28 83 94 88</span>
                      </div>
                    </a>

                    <a
                      href="mailto:bgv@terapien.dk"
                      className="flex items-center gap-4 p-3 rounded-2xl hover:bg-brand-offwhite/80 transition-all duration-300 group"
                    >
                      <div className="w-10 h-10 rounded-full bg-brand-sage/10 flex items-center justify-center text-brand-sage group-hover:bg-brand-sage group-hover:text-brand-offwhite transition-all duration-300">
                        <i className="fa-solid fa-envelope text-sm" />
                      </div>
                      <div>
                        <span className="text-xs text-brand-gray/50 block">Send en mail</span>
                        <span className="text-brand-dark font-medium group-hover:text-brand-sage transition-colors break-all">bgv@terapien.dk</span>
                      </div>
                    </a>

                    <div className="flex items-center gap-4 p-3 rounded-2xl">
                      <div className="w-10 h-10 rounded-full bg-brand-sage/10 flex items-center justify-center text-brand-sage">
                        <i className="fa-solid fa-location-dot text-sm" />
                      </div>
                      <div>
                        <span className="text-xs text-brand-gray/50 block">Praksis adresse</span>
                        <span className="text-brand-dark font-medium">Dronningemaen 68, Svendborg</span>
                      </div>
                    </div>
                  </div>

                  <div className="pt-2">
                    <button
                      onClick={() => navigate('/kontakt')}
                      className="w-full bg-brand-sage hover:bg-brand-sage/90 text-brand-offwhite font-medium py-4 px-6 rounded-full shadow-lg shadow-brand-sage/10 hover:shadow-xl hover:shadow-brand-sage/20 active:scale-[0.98] transition-all duration-300 text-center block"
                    >
                      Book en samtale <i className="fa-solid fa-arrow-right ml-2 text-xs" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Methods Section */}
        <section className="bg-brand-sand/15 py-20 lg:py-28 border-t border-brand-sand/30">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
              <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-bold">
                Terapeutiske Metoder
              </span>
              <h2 className="text-3xl sm:text-4xl font-serif font-bold text-brand-dark">
                {parsed?.methodsHeading}
              </h2>
              <div className="w-12 h-[2px] bg-brand-sage/40 mx-auto mt-4" />
            </div>

            {/* Methods Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-10">
              {parsed?.methods.map((method, index) => {
                const isFullWidth =
                  (index === parsed.methods.length - 1 && parsed.methods.length % 2 !== 0) ||
                  method.title.toLowerCase().includes('adfærd')

                return (
                  <div
                    key={index}
                    className={`bg-brand-offwhite border border-brand-sand/40 rounded-3xl p-8 lg:p-10 shadow-[0_8px_30px_rgb(0,0,0,0.01)] hover:shadow-[0_15px_40px_rgb(0,0,0,0.04)] hover:-translate-y-1 transition-all duration-300 flex flex-col gap-6 group ${
                      isFullWidth ? 'md:col-span-2' : ''
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 rounded-2xl bg-brand-sage/10 flex items-center justify-center text-brand-sage group-hover:bg-brand-sage group-hover:text-brand-offwhite transition-all duration-300">
                        <i className={`fa-solid ${getMethodIcon(method.title)} text-xl`} />
                      </div>
                      <h3 className="text-xl lg:text-2xl font-serif font-bold text-brand-dark">
                        {method.title}
                      </h3>
                    </div>

                    <div
                      className="prose max-w-none text-brand-gray/80 text-sm sm:text-base"
                      dangerouslySetInnerHTML={{ __html: method.paragraphs.join('') }}
                    />
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Closing CTA */}
        <section className="py-20 bg-brand-offwhite border-t border-brand-sand/30 text-center">
          <div className="max-w-3xl mx-auto px-6 flex flex-col items-center gap-6">
            <h2 className="text-2xl sm:text-3xl font-serif font-bold text-brand-dark">
              Klar til at tage det første skridt?
            </h2>
            <p className="text-sm sm:text-base text-brand-gray/80 max-w-xl leading-relaxed">
              Du behøver ikke stå alene med det svære. Kontakt mig for en uforpligtende samtale, hvor vi i ro og mag finder ud af, hvad du har brug for.
            </p>
            <div className="mt-2 flex flex-wrap justify-center gap-4">
              <button
                onClick={() => navigate('/kontakt')}
                className="bg-brand-dark hover:bg-brand-dark/90 text-brand-offwhite text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:shadow-lg hover:scale-[1.02] active:scale-[0.98]"
              >
                Book en samtale
              </button>
              <button
                onClick={() => navigate('/priser')}
                className="bg-brand-offwhite border border-brand-dark/30 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
              >
                Se priser
              </button>
              <button
                onClick={() => navigate('/kontakt')}
                className="bg-brand-sand/30 border border-brand-sand/70 hover:border-brand-dark text-brand-dark text-sm font-semibold uppercase tracking-wider px-8 py-4 rounded-full transition-all duration-300 hover:bg-white hover:scale-[1.02] active:scale-[0.98]"
              >
                Kontakt mig
              </button>
            </div>
          </div>
        </section>
      </div>
    )
  }

  // Render Policy Pages (Privacy Policy, Cookie Policy)
  const parsedPolicy = parsePolicyContent(page.indhold)

  return (
    <div className="pt-[72px] min-h-screen bg-brand-offwhite">
      {/* Policy Hero */}
      <section className="bg-brand-sand/20 py-16 lg:py-20 border-b border-brand-sand/30">
        <div className="max-w-7xl mx-auto px-6">
          <span className="text-xs uppercase tracking-[0.25em] text-brand-sage font-bold block mb-3">
            Vilkår & Betingelser
          </span>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-brand-dark">
            {page.titel}
          </h1>
        </div>
      </section>

      {/* Policy Content with Sticky Table of Contents */}
      <section className="py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            {/* Left: Sticky Table of Contents */}
            <div className="lg:col-span-4 lg:sticky lg:top-28 space-y-6">
              <div className="bg-brand-sand/10 border border-brand-sand/30 rounded-3xl p-6 sm:p-8">
                <h3 className="text-xs uppercase tracking-[0.2em] text-brand-sage font-bold mb-6">
                  Indholdsfortegnelse
                </h3>
                <nav className="space-y-3">
                  {parsedPolicy?.headings.map((heading) => (
                    <a
                      key={heading.id}
                      href={`#${heading.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        document.getElementById(heading.id)?.scrollIntoView({
                          behavior: 'smooth',
                          block: 'start'
                        })
                      }}
                      className={`block text-sm font-medium transition-all duration-200 hover:text-brand-sage ${
                        activeHeading === heading.id
                          ? 'text-brand-sage pl-3 border-l-2 border-brand-sage'
                          : 'text-brand-gray/60 pl-3 border-l-2 border-transparent'
                      }`}
                    >
                      {heading.text}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            {/* Right: Policy Content */}
            <div className="lg:col-span-8">
              <div
                className="prose text-brand-gray/80 text-sm sm:text-base"
                dangerouslySetInnerHTML={{ __html: parsedPolicy?.html }}
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}
